import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { money, num, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Loader2, Sun } from "lucide-react";
import {
  ProposalPie, ProposalDonut, ProposalBar, ProposalAreaStack, ProposalCashFlow,
  ProposalStackedBar, ProposalGantt, WeatherCalendar, FigCaption,
} from "@/components/proposal/charts";

export default function ProposalPrint() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [status, setStatus] = useState("loading");
  const [brand, setBrand] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [driveOn, setDriveOn] = useState(false);

  // Client-facing print docs are always light — never inherit the app's dark mode.
  useEffect(() => {
    const html = document.documentElement;
    const wasDark = html.classList.contains("dark");
    html.classList.remove("dark");
    return () => { if (wasDark) html.classList.add("dark"); };
  }, []);

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/bids/${bidId}/proposal/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(doc?.name || "Proposal").replace(/[^A-Za-z0-9._-]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      const { toast } = await import("sonner");
      toast.success(driveOn ? "PDF downloaded · a copy was filed to Google Drive (To Client)" : "PDF downloaded");
    } catch {
      const { toast } = await import("sonner");
      toast.error("PDF render failed — use Print as a fallback.");
    } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    api.get(`/bids/${bidId}/proposal`).then((r) => {
      if (r.data.source === "provided" && r.data.provided) {
        window.location.replace(`${API_BASE}/documents/${r.data.provided.document_id}/download?inline=true`);
        return;
      }
      setStatus(r.data.status);
      if (r.data.exists) setDoc(r.data);
    }).catch(() => setStatus("error"));
    api.get("/proposal-branding").then((r) => setBrand(r.data)).catch(() => {});
    api.get("/storage/drive/status").then((r) => setDriveOn(!!r.data?.enabled)).catch(() => {});
  }, [bidId]);

  useEffect(() => {
    if (!brand) return;
    const fams = [brand.font_heading, brand.font_body].filter(Boolean);
    const id = "brand-fonts";
    let l = document.getElementById(id);
    if (!l) { l = document.createElement("link"); l.id = id; l.rel = "stylesheet"; document.head.appendChild(l); }
    l.href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${f.replace(/ /g, "+")}:wght@400;600;700`).join("&")}&display=swap`;
  }, [brand]);

  if (!doc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500">
        {status === "generating" ? <Loader2 className="w-6 h-6 animate-spin text-blue-600" /> : <Sun className="w-6 h-6 text-slate-300" />}
        <div className="text-sm">{status === "generating" ? "Proposal is still generating…" : "No proposal generated yet. Go back and generate it first."}</div>
        <Button variant="outline" onClick={() => navigate(`/bids/${bidId}`)}><ArrowLeft className="w-4 h-4 mr-1.5" /> Back to bid</Button>
      </div>
    );
  }

  const s = doc.sections || {};
  const f = doc.figures || {};
  const b = brand || {};
  const term = { ...b, ...(doc.commercial || {}) };
  const primary = b.primary || "#003E87";
  const accent = b.accent || "#F4C400";
  const ink = b.ink || "#0F172A";
  const company = b.company_name || "";
  const headFont = b.font_heading || "Space Grotesk";
  const bodyFont = b.font_body || "Inter";
  const today = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const totals = doc.totals || {};
  const perW = totals.cost_per_watt ? `$${totals.cost_per_watt.toFixed(4)} / Watt` : "";

  // ── W8 Detail Settings: gate every optional section; price table + total stay on ──
  const set = doc.settings || {};
  const on = (k) => !!set[k];
  const dec = [0, 1, 2].includes(set.round_decimals) ? set.round_decimals : 0;
  const mUSD = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
  const grandTotal = on("round_total") ? Math.round((totals.total || 0) / 1000) * 1000 : (totals.total || 0);
  const rawRows = doc.pricing_rows || [];
  const groupKey = ({ discipline: "discipline", cost_code: "cost_code", scope: "group", groups: "group" })[set.group_by] || "discipline";
  const groupedRows = Object.values(rawRows.reduce((acc, r) => {
    const k = `${r[groupKey] || "Other"}`;
    (acc[k] ||= { name: k, subtotal: 0 }).subtotal += r.subtotal || 0;
    return acc;
  }, {}));
  const perLineCols = on("unit_price_col") ? 4 : 3;
  // ── Subgroups: when grouping by scope, show group totals with client-visible subgroup
  // sub-rows beneath them; hidden (or unassigned) subgroups roll up silently into the group. ──
  const groupByScope = set.group_by === "scope" || set.group_by === "groups";
  const collapsed1 = on("groups_only") || groupByScope;
  const subCfg = doc.scope_subgroups || [];
  const subVisible = (g, s) => { const e = subCfg.find((x) => x.group === g && x.name === s); return e ? e.show_on_proposal !== false : true; };
  const subClr = (g, s) => (subCfg.find((x) => x.group === g && x.name === s) || {}).color;
  const scopeTree = (() => {
    const idx = {}; const order = [];
    rawRows.forEach((r) => {
      const g = `${r.group || "Other"}`;
      let node = idx[g];
      if (!node) { node = idx[g] = { name: g, subtotal: 0, subs: {}, subOrder: [] }; order.push(node); }
      node.subtotal += r.subtotal || 0;
      const sn = r.subgroup || "";
      if (sn && subVisible(g, sn)) {
        let sub = node.subs[sn];
        if (!sub) { sub = node.subs[sn] = { name: sn, subtotal: 0, color: subClr(g, sn) }; node.subOrder.push(sn); }
        sub.subtotal += r.subtotal || 0;
      }
    });
    return order;
  })();
  // De-duped palette of client-visible subgroups (by name) for a legend under the pricing table.
  const legendSubs = [...new Map(
    scopeTree.flatMap((g) => g.subOrder.map((sn) => g.subs[sn])).filter((s) => s && s.color).map((s) => [s.name, s])
  ).values()];
  const showPartB = ["fig_gantt", "schedule_table", "schedule_baseline", "execution_strategy", "man_loaded_table",
    "fig_monthly_manpower", "fig_cash_flow", "fig_production", "fig_equipment_share", "fig_equipment_monthly",
    "fig_equipment_peak", "fig_weather", "clarifications"].some(on);

  const ctx = { primary, accent, ink, company, headFont, b };

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: `'${bodyFont}',sans-serif` }}>
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/bids/${bidId}`)}><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
        <div className="text-sm text-slate-500">Proposal &amp; Execution Plan · generated {fmtDate(doc.generated_at)}{doc.ai_used ? " · AI narrative" : ""}</div>
        <div className="flex-1" />
        <Button variant="outline" onClick={downloadPdf} disabled={pdfBusy} data-testid="proposal-server-pdf">
          {pdfBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} {pdfBusy ? "Rendering…" : "Export PDF"}
        </Button>
        <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => window.print()} data-testid="proposal-download-pdf">
          <Download className="w-4 h-4 mr-1.5" /> Print
        </Button>
      </div>

      <style>{`
        .print-page .font-display{font-family:'${headFont}',sans-serif}
        @media print {
          @page { size: Letter; margin: 0; }
          .no-print{display:none!important}
          .print-page{box-shadow:none!important;margin:0!important;min-height:10.55in!important;break-after:page;page-break-after:always}
          .print-page:last-child{break-after:auto;page-break-after:auto}
          body{background:#fff}
        }
      `}</style>

      {doc.warning && <div className="no-print max-w-3xl mx-auto mt-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-4 py-2" data-testid="proposal-warning">{doc.warning}</div>}

      <div data-testid="proposal-print-doc">
        {/* ============================= PART A ============================= */}
        <Cover ctx={ctx} kicker="PROPOSAL" doc={doc} perW={perW} today={today} totalStr={money(totals.total)} testid="proposal-cover-a" />

        {/* A1 — intro / contacts */}
        {(on("executive_summary") || on("customer_block") || on("estimator_block")) && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A1" title={`Proposal — ${today}`} />
          <div className="grid grid-cols-2 gap-8 mb-6">
            {on("estimator_block") && <Contact label="Prepared by" name={company} lines={[b.address, b.phone, b.email, b.website]} accent={accent} />}
            {on("customer_block") && <Contact label="Prepared for" name={doc.client_name} lines={[doc.location]} accent={accent} />}
          </div>
          {on("estimator_block") && b.credentials?.length > 0 && (
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 mb-6 text-xs" data-testid="proposal-credentials">
              {b.credentials.map((c, i) => (
                <div key={i} className="flex justify-between gap-3 border-b border-slate-100 py-1"><span className="text-slate-500">{c.label}</span><span className="font-medium text-slate-800 text-right">{c.value}</span></div>
              ))}
            </div>
          )}
          {on("executive_summary") && <Prose>{s.executive_summary}</Prose>}
        </Page>
        )}

        {/* A2 — project at a glance */}
        {(on("glance") || on("project_understanding")) && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A2" title="Project at a Glance" />
          {on("glance") && (
          <div className="grid grid-cols-3 gap-3" data-testid="proposal-glance">
            {(f.glance || []).map((g, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-4">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{g.label}</div>
                <div className="font-display text-2xl font-bold mt-1" style={{ color: ink }}>{g.value}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{g.note}</div>
              </div>
            ))}
          </div>
          )}
          {on("project_understanding") && <div className="mt-6"><Prose>{s.project_understanding}</Prose></div>}
        </Page>
        )}

        {/* A3 — scope of work & execution plan (W11) + Fig 1 */}
        {(on("execution_plan") || (on("fig_scope_mh") && f.scope_mh?.length > 0)) && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A3" title="Scope of Work & Execution Plan" />
          {on("execution_plan") && (Array.isArray(doc.execution_phases) && doc.execution_phases.length > 0
            ? doc.execution_phases.map((p, i) => {
                const nar = (s.execution_plan || []).find((x) => x.phase === p.phase)?.narrative;
                return (
                  <div key={i} className="mb-4 break-inside-avoid" data-testid={`proposal-exec-phase-${i}`}>
                    <div className="flex items-baseline justify-between border-b border-slate-100 pb-1 mb-1">
                      <div className="text-sm font-semibold" style={{ color: ink }}>{i + 1}. {p.phase}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{[p.crews ? `${p.crews} crew(s)` : null, p.days ? `${p.days} WD` : null, p.daily_target || null].filter(Boolean).join(" · ")}</div>
                    </div>
                    {nar && <div className="text-sm text-slate-700 leading-relaxed">{nar}</div>}
                    {p.equipment?.length > 0 && <div className="text-[11px] text-slate-500 mt-1"><span className="uppercase tracking-wide text-slate-400">Equipment:</span> {p.equipment.join(", ")}</div>}
                    <div className="text-[11px] text-slate-500 mt-0.5"><span className="uppercase tracking-wide text-slate-400">QA/QC:</span> {p.qa}</div>
                  </div>
                );
              })
            : (Array.isArray(s.execution_plan) && s.execution_plan.map((p, i) => (
                <div key={i} className="mb-3 break-inside-avoid">
                  <div className="text-sm font-semibold" style={{ color: ink }}>{p.phase}</div>
                  <div className="text-sm text-slate-700 leading-relaxed mt-0.5">{p.narrative}</div>
                </div>
              ))))}
          {on("fig_scope_mh") && f.scope_mh?.length > 0 && (
            <div className="mt-6">
              <ProposalPie data={f.scope_mh} brand={b} valueFmt={(v) => `${num(v)} MH`} />
              <FigCaption n="1">Scope distribution by man-hours ({num(f.total_mh)} total)</FigCaption>
            </div>
          )}
        </Page>
        )}

        {/* A4 — scope pricing (always shown — the price-only baseline) */}
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A4" title="Scope Pricing" />
          <table className="w-full text-sm mb-4" data-testid="proposal-pricing-table">
            <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b-2" style={{ borderColor: primary }}>
              <th className="text-left py-1.5 font-medium">{collapsed1 ? "Scope" : "Item"}</th>
              {!collapsed1 && <th className="text-left font-medium">Discipline</th>}
              {!collapsed1 && <th className="text-right font-medium">Quantity</th>}
              {!collapsed1 && on("unit_price_col") && <th className="text-right font-medium">Unit price</th>}
              <th className="text-right font-medium">Amount</th></tr></thead>
            <tbody>
              {!on("lump_sum") && (groupByScope
                ? scopeTree.map((g, gi) => (
                    <React.Fragment key={gi}>
                      <tr className="border-b border-slate-100 font-semibold" data-testid={`proposal-scope-row-${gi}`}><td className="py-1.5">{g.name}</td><td className="text-right font-mono">{mUSD(g.subtotal)}</td></tr>
                      {g.subOrder.map((sn, si) => { const sub = g.subs[sn]; return (
                        <tr key={si} className="border-b border-slate-50 text-slate-500" data-testid={`proposal-subgroup-row-${gi}-${si}`}>
                          <td className="py-1 pl-6"><span className="inline-flex items-center gap-1.5">{sub.color && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: sub.color }} />}{sub.name}</span></td>
                          <td className="text-right font-mono">{mUSD(sub.subtotal)}</td>
                        </tr>); })}
                    </React.Fragment>
                  ))
                : on("groups_only")
                  ? groupedRows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{r.name}</td><td className="text-right font-mono">{mUSD(r.subtotal)}</td></tr>
                    ))
                  : rawRows.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5">{r.name}</td><td className="capitalize text-slate-500">{r.discipline}</td>
                        <td className="text-right font-mono">{num(r.quantity)} {r.unit}</td>
                        {on("unit_price_col") && <td className="text-right font-mono text-slate-500">{r.quantity ? mUSD(r.subtotal / r.quantity) : "—"}</td>}
                        <td className="text-right font-mono">{mUSD(r.subtotal)}</td>
                      </tr>)))}
              {!on("lump_sum") && !collapsed1 && set.group_by === "discipline" && (doc.discipline_subtotals || []).map((d, i) => (
                <tr key={`d${i}`} className="text-slate-500"><td className="py-1 capitalize" colSpan={perLineCols}>{d.discipline} subtotal</td><td className="text-right font-mono">{mUSD(d.subtotal)}</td></tr>))}
              <tr className="font-bold border-t-2" style={{ borderColor: ink }}><td className="py-2" colSpan={collapsed1 ? 1 : perLineCols}>TOTAL FIRM FIXED PRICE</td><td className="text-right font-mono">{mUSD(grandTotal)}</td></tr>
            </tbody>
          </table>
          {groupByScope && legendSubs.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 -mt-2 mb-4 text-[11px] text-slate-500" data-testid="proposal-subgroup-legend">
              <span className="uppercase tracking-wide text-slate-400">Legend</span>
              {legendSubs.map((s, i) => (
                <span key={i} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />{s.name}</span>
              ))}
            </div>
          )}
          {on("fig_price_by_group") && f.price_groups?.length > 0 && (
            <div className="mt-4">
              <ProposalDonut data={f.price_groups} brand={b} />
              <FigCaption n="2">Contract price distribution by scope</FigCaption>
            </div>
          )}
        </Page>

        {/* A4b — client-optional alternates */}
        {on("alternates") && (doc.totals?.alternates || []).filter((a) => a.show_on_proposal !== false).length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="A4b" title="Alternates — Client Options" />
            <p className="text-sm text-slate-600 mb-3">Optional add/deduct scope, priced separately. These are not included in the firm fixed price unless accepted.</p>
            <table className="w-full text-sm" data-testid="proposal-alternates-table">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b-2" style={{ borderColor: primary }}>
                <th className="text-left py-1.5 font-medium">Alternate</th><th className="text-left font-medium">Type</th><th className="text-right font-medium">Amount</th></tr></thead>
              <tbody>
                {doc.totals.alternates.filter((a) => a.show_on_proposal !== false).map((a, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5">{a.name}{a.detail ? <span className="block text-[11px] text-slate-400 font-mono">{a.detail}</span> : null}</td>
                    <td className="capitalize text-slate-500">{a.type}</td>
                    <td className="text-right font-mono">{a.type === "deduct" ? "−" : "+"}{money(a.amount)}</td>
                  </tr>))}
              </tbody>
            </table>
          </Page>
        )}

        {/* A5 removed — inclusions render on A8 (avoids duplication) */}

        {/* A6 — unit basis & build pace */}
        {((on("unit_basis") && f.unit_basis?.length > 0) || (on("build_pace") && f.build_pace?.length > 0)) && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="A6" title="Unit Basis & Build Pace" />
            <div className="grid grid-cols-2 gap-8">
              <KVTable title="Price basis" rows={on("unit_basis") ? f.unit_basis : []} />
              <KVTable title="Build pace" rows={on("build_pace") ? f.build_pace : []} />
            </div>
          </Page>
        )}

        {/* A7 — bond + warranty */}
        {(on("bond") || on("warranty")) && (
        <Page ctx={ctx}>
          {on("bond") && (<>
          <SectionTitle ctx={ctx} n="A7" title="Performance & Payment Bond" />
          <p className="text-sm text-slate-600 mb-3">Optional add-on. Provided at the rates below if elected by the Owner.</p>
          <DerivationTable rows={f.bond?.rows} />
          <TotalLine label="Contract price with bond" value={money(f.bond?.price_with_bond)} accent={accent} />
          </>)}
          {on("warranty") && (
          <div className={on("bond") ? "mt-8" : ""}>
            <SectionTitle ctx={ctx} n="A7b" title="Extended Warranty" />
            <p className="text-sm text-slate-600 mb-3">Optional add-on extending workmanship warranty for the term below.</p>
            <DerivationTable rows={f.warranty?.rows} />
            <TotalLine label="Contract price with extended warranty" value={money(f.warranty?.price_with_warranty)} accent={accent} />
          </div>
          )}
        </Page>
        )}

        {/* A8 — included */}
        {on("inclusions") && doc.inclusions?.length > 0 && (
          <Page ctx={ctx}><SectionTitle ctx={ctx} n="A8" title="Included (+)" /><NumberedList items={doc.inclusions} /></Page>
        )}
        {/* A9 — excluded */}
        {on("exclusions") && doc.exclusions?.length > 0 && (
          <Page ctx={ctx}><SectionTitle ctx={ctx} n="A9" title="Excluded (−)" /><NumberedList items={doc.exclusions} /></Page>
        )}
        {/* A10 — assumptions + bid confidence */}
        {(on("clarifications") || on("bid_confidence")) && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A10" title="Assumptions & Clarifications" />
          {on("clarifications") && (doc.clarifications?.length > 0 ? <NumberedList items={doc.clarifications} /> : <Prose>{s.schedule_notes}</Prose>)}
          {on("bid_confidence") && (
          <div className="mt-6">
            <div className="text-sm font-semibold mb-1" style={{ color: ink }}>A10b · Bid Confidence</div>
            <Prose>{s.bid_confidence}</Prose>
          </div>
          )}
        </Page>
        )}

        {/* A11 — notes / schedule basis */}
        {(on("schedule_narrative") || (on("milestones") && doc.milestones?.length > 0)) && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A11" title="Notes" />
          {on("schedule_narrative") && <Prose>{s.schedule_narrative}</Prose>}
          {on("milestones") && doc.milestones?.length > 0 && (
            <table className="w-full text-sm mt-4">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200"><th className="text-left py-1.5 font-medium">Milestone</th><th className="text-left font-medium">Date</th></tr></thead>
              <tbody>{doc.milestones.map((m, i) => <tr key={i} className="border-b border-slate-100"><td className="py-1.5">{m.label}</td><td className="font-mono text-slate-600">{fmtDate(m.date)}</td></tr>)}</tbody>
            </table>
          )}
        </Page>
        )}

        {/* A14 — summary & total (the price) */}
        {on("summary_total") && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A14" title="Summary & Total" />
          <div className="rounded-lg p-6 text-white" style={{ background: primary }}>
            <div className="text-xs uppercase tracking-wide opacity-70">Firm fixed price</div>
            <div className="font-display text-4xl font-bold mt-1">{mUSD(grandTotal)}</div>
            <div className="text-sm opacity-80 font-mono mt-1">{on("show_per_watt") ? perW : ""}{on("show_per_mw") && totals.total && doc.capacity_mw ? `${on("show_per_watt") ? " · " : ""}$${(totals.total / doc.capacity_mw).toLocaleString("en-US", { maximumFractionDigits: 0 })}/MWdc` : ""}</div>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-6">
            <KVTable title="Optional add-ons" rows={[
              on("bond") ? { label: "Performance & payment bond", value: `+ ${money(f.bond?.premium)}` } : null,
              on("warranty") ? { label: "Extended warranty", value: `+ ${money(f.warranty?.premium)}` } : null,
            ].filter(Boolean)} />
            <KVTable title="Payment & mobilization" rows={[
              { label: "Mobilization", value: `${term.mobilization_pct}%` },
              { label: "Retainage", value: `${term.retainage_pct}%` },
              { label: "Payment terms", value: `Net ${term.payment_net_days} days` },
              { label: "Proposal validity", value: `${term.validity_days} days` },
            ]} />
          </div>
        </Page>
        )}

        {/* A15 — acceptance */}
        {on("acceptance") && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="A15" title="Acceptance" />
          <p className="text-sm text-slate-600 mb-8">This proposal is valid for {term.validity_days} days from {today}. By signing below, the parties agree to the scope, schedule and firm fixed price set out herein.</p>
          <div className="grid grid-cols-2 gap-10">
            {["Accepted for " + (doc.client_name || "Client"), company || "Contractor"].map((label, i) => (
              <div key={i}>
                <div className="border-b border-slate-400 h-10" /><div className="text-xs text-slate-500 mt-1">Signature</div>
                <div className="border-b border-slate-400 h-8 mt-4" /><div className="text-xs text-slate-500 mt-1">Name / Title / Date</div>
                <div className="text-xs mt-3 font-medium" style={{ color: ink }}>{label}</div>
              </div>
            ))}
          </div>
        </Page>
        )}

        {/* ============================= PART B ============================= */}
        {showPartB && (<>
        <Cover ctx={ctx} kicker="EXECUTION PLAN" doc={doc} perW={perW} today={today} totalStr={`${f.months_active || ""} month programme`} testid="proposal-cover-b" />

        {/* B2 — project basis + Fig 3 gantt */}
        {on("fig_gantt") && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="B2" title="Project Basis & Execution Summary" />
          <div className="grid grid-cols-4 gap-2 mb-5">
            {(f.glance || []).slice(1, 9).map((g, i) => (
              <div key={i} className="rounded border border-slate-200 p-3">
                <div className="text-[9px] uppercase tracking-wide text-slate-400">{g.label}</div>
                <div className="font-display text-lg font-bold" style={{ color: ink }}>{g.value}</div>
              </div>
            ))}
          </div>
          {doc.programme?.tasks?.length > 0 && (
            <>
              <ProposalGantt tasks={doc.programme.tasks} projectEnd={doc.programme.project_end} startDate={f.start_date} brand={b} testid="proposal-gantt" />
              <FigCaption n="3">Execution timeline by activity — {doc.programme.project_end} calendar days</FigCaption>
            </>
          )}
        </Page>
        )}

        {/* B2b — client-safe construction schedule (pace per unit; crew size never shown) */}
        {on("schedule_table") && Array.isArray(f.client_schedule) && f.client_schedule.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B2b" title="Construction Schedule" />
            <p className="text-sm text-slate-600 mb-3">Indicative durations by scope — {f.client_schedule_days} working days overall. Pace is shown per unit; crews are sized to hold this schedule.</p>
            <table className="w-full text-sm mb-3">
              <thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-200"><th className="py-1">Scope</th><th className="py-1 text-right">Pace</th><th className="py-1 text-right">Duration</th><th className="py-1 w-2/5">Timeline</th></tr></thead>
              <tbody>
                {f.client_schedule.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`proposal-schedule-row-${i}`}>
                    <td className="py-1.5">{r.scope}</td>
                    <td className="py-1.5 text-right font-mono text-slate-600">{r.pace ? `${r.pace} ${r.unit || "u"}/day` : "—"}</td>
                    <td className="py-1.5 text-right font-mono">{r.days} d</td>
                    <td className="py-1.5"><div className="h-3 bg-slate-100 rounded relative"><div className="absolute h-3 rounded" style={{ left: `${(r.start / (f.client_schedule_days || 1)) * 100}%`, width: `${Math.max((r.days / (f.client_schedule_days || 1)) * 100, 2)}%`, background: b.primary || "#003E87" }} /></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FigCaption n="3b">Scope durations — {f.client_schedule_days} working days (parallel lanes overlap)</FigCaption>
          </Page>
        )}

        {/* B2c — schedule vs baseline slippage (owner-facing) */}
        {on("schedule_baseline") && f.schedule_baseline && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B2c" title="Schedule vs Baseline" />
            <p className="text-sm text-slate-600 mb-3">Baseline finish <b>{f.schedule_baseline.baseline_end}</b> · current finish <b>{f.schedule_baseline.current_end}</b>
              {f.schedule_baseline.end_slip != null && <span className={f.schedule_baseline.end_slip > 0 ? "text-rose-600 font-semibold" : "text-emerald-600 font-semibold"}> — {f.schedule_baseline.end_slip > 0 ? `${f.schedule_baseline.end_slip} days late` : f.schedule_baseline.end_slip < 0 ? `${-f.schedule_baseline.end_slip} days ahead` : "on plan"}</span>}.
            </p>
            {f.schedule_baseline.rows?.length > 0 && (
              <table className="w-full text-sm mb-3"><thead><tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-200"><th className="py-1">Scope</th><th className="py-1">Baseline finish</th><th className="py-1">Current finish</th><th className="py-1 text-right">Slip</th></tr></thead>
                <tbody>{f.schedule_baseline.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`proposal-baseline-row-${i}`}><td className="py-1.5">{r.scope}</td><td className="py-1.5 text-slate-600">{r.baseline_end}</td><td className="py-1.5 text-slate-600">{r.current_end}</td><td className={`py-1.5 text-right font-mono ${r.slip > 0 ? "text-rose-600" : r.slip < 0 ? "text-emerald-600" : "text-slate-400"}`}>{r.slip > 0 ? `+${r.slip}d` : r.slip < 0 ? `${r.slip}d` : "0"}</td></tr>
                ))}</tbody></table>
            )}
            <FigCaption n="3c">Slippage vs the saved baseline schedule</FigCaption>
          </Page>
        )}

        {/* B3 — execution strategy */}
        {on("execution_strategy") && Array.isArray(s.execution_strategy) && s.execution_strategy.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B3" title="Execution Strategy" />
            {s.execution_strategy.map((p, i) => (
              <div key={i} className="mb-3 break-inside-avoid">
                <div className="text-sm font-semibold" style={{ color: ink }}>{p.phase}</div>
                <div className="text-sm text-slate-700 leading-relaxed mt-0.5">{p.narrative}</div>
              </div>
            ))}
          </Page>
        )}

        {/* B4 — man-loaded activity schedule + Fig 4 */}
        {on("man_loaded_table") && (
        <Page ctx={ctx}>
          <SectionTitle ctx={ctx} n="B4" title="Man-Loaded Activity Schedule" />
          <table className="w-full text-sm mb-4">
            <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
              <th className="text-left py-1.5 font-medium">Activity</th><th className="text-left font-medium">Discipline</th>
              <th className="text-right font-medium">Crews</th><th className="text-right font-medium">Work-days</th><th className="text-right font-medium">Man-days</th></tr></thead>
            <tbody>
              {(doc.schedule_phases || []).map((p, i) => {
                const md = f.man_days?.find((m) => m.label && (p.name || "").toLowerCase().includes(m.label.toLowerCase().split(" ")[0]));
                return (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1.5">{p.name}</td><td className="capitalize text-slate-500">{p.discipline}</td>
                    <td className="text-right font-mono">{p.crews}</td><td className="text-right font-mono">{p.days}</td>
                    <td className="text-right font-mono">{num(p.crew_days)}</td>
                  </tr>);
              })}
              <tr className="font-bold border-t-2 border-slate-300"><td className="py-2" colSpan={4}>Total man-days</td><td className="text-right font-mono">{num(f.total_man_days)}</td></tr>
            </tbody>
          </table>
          {on("fig_man_days") && f.man_days?.length > 0 && (
            <><ProposalBar data={f.man_days} brand={b} valueFmt={(v) => `${num(v)} MD`} />
              <FigCaption n="4">Man-days by activity (sorted)</FigCaption></>
          )}
        </Page>
        )}

        {/* B5 — monthly loaded manpower + Fig 5 */}
        {on("fig_monthly_manpower") && f.monthly_manpower?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B5" title="Monthly Loaded Manpower" />
            <div className="grid grid-cols-3 gap-3 mb-4">
              <MiniStat label="Peak headcount" value={num(doc.peak_headcount)} ink={ink} />
              <MiniStat label="Peak month" value={f.peak_month || "—"} ink={ink} />
              <MiniStat label="Months active" value={num(f.months_active)} ink={ink} />
            </div>
            <ProposalAreaStack data={f.monthly_manpower} keys={f.manpower_phases} brand={b} label="Headcount" />
            <FigCaption n="5">Monthly loaded manpower by phase</FigCaption>
          </Page>
        )}

        {/* B5b — cash flow + Fig 6 */}
        {on("fig_cash_flow") && f.cash_flow?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B5b" title="Cash Flow Forecast" />
            <p className="text-sm text-slate-600 mb-3">Progress billing is spread by monthly labor loading, with {b.retainage_pct}% retainage withheld and released at closeout; collections lag billing by Net {b.payment_net_days} days.</p>
            <ProposalCashFlow data={f.cash_flow} brand={b} />
            <FigCaption n="6">Monthly net cash with billed vs. collected curves</FigCaption>
            <table className="w-full text-xs mt-4">
              <thead><tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="text-left py-1 font-medium">Month</th><th className="text-right font-medium">Billing</th>
                <th className="text-right font-medium">Retainage</th><th className="text-right font-medium">Net cash</th>
                <th className="text-right font-medium">Collected</th><th className="text-right font-medium">Billed %</th></tr></thead>
              <tbody>{f.cash_flow.map((c, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1">{c.key}</td><td className="text-right font-mono">{money(c.billing)}</td>
                  <td className="text-right font-mono text-slate-500">{money(c.retainage)}</td><td className="text-right font-mono">{money(c.net_cash)}</td>
                  <td className="text-right font-mono">{money(c.collected)}</td><td className="text-right font-mono text-slate-500">{c.billed_pct}%</td>
                </tr>))}</tbody>
            </table>
          </Page>
        )}

        {/* B6 — scope durations + Fig 7 */}
        {on("fig_production") && f.scope_durations?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B6" title="Scope Durations & Daily Production" />
            <table className="w-full text-sm mb-4">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
                <th className="text-left py-1.5 font-medium">Scope</th><th className="text-right font-medium">Total quantity</th>
                <th className="text-right font-medium">Work-days</th><th className="text-right font-medium">Required / day</th></tr></thead>
              <tbody>{f.scope_durations.map((d, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5">{d.scope}</td><td className="text-right font-mono">{num(d.quantity)} {d.unit}</td>
                  <td className="text-right font-mono">{d.work_days}</td><td className="text-right font-mono">{num(d.per_day)} {d.unit}/day</td>
                </tr>))}</tbody>
            </table>
            <div className="grid grid-cols-3 gap-3">
              {f.production.map((p, i) => (
                <div key={i} className="rounded-lg border p-4 text-center" style={{ borderColor: primary }}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">{p.scope}</div>
                  <div className="font-display text-2xl font-bold mt-1" style={{ color: primary }}>{num(p.rate)}</div>
                  <div className="text-[11px] text-slate-500">{p.unit} / day · {p.work_days} WD</div>
                </div>))}
            </div>
            <FigCaption n="7">Daily production rate by scope</FigCaption>
          </Page>
        )}

        {/* B7 — equipment & site support + Fig 8 */}
        {on("fig_equipment_share") && f.equipment_share?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B7" title="Equipment & Site Support" />
            <ProposalDonut data={f.equipment_share} brand={b} valueFmt={(v) => `${num(v)} pc`} />
            <FigCaption n="8">Equipment share by phase — {num(f.total_equipment)} pieces</FigCaption>
          </Page>
        )}

        {/* B8 — equipment-loaded schedule + Fig 9 */}
        {on("fig_equipment_monthly") && f.equipment_monthly?.length > 0 && f.equipment_phases?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B8" title="Equipment-Loaded Schedule by Scope" />
            <ProposalStackedBar data={f.equipment_monthly} keys={f.equipment_phases} brand={b} label="Pieces" />
            <FigCaption n="9">Equipment deployment by phase (monthly)</FigCaption>
          </Page>
        )}

        {/* B11 — peak equipment by type + Fig 10 */}
        {on("fig_equipment_peak") && f.equipment_by_type?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B11" title="Peak Equipment by Type" />
            <p className="text-sm text-slate-600 mb-3">Up to {num(f.peak_equipment_total)} pieces on site at peak deployment.</p>
            <ProposalBar data={f.equipment_by_type} brand={b} valueFmt={(v) => `${num(v)}`} color={accent} />
            <FigCaption n="10">Peak equipment by type</FigCaption>
          </Page>
        )}

        {/* B12 — schedule notes + Fig 11 weather */}
        {on("fig_weather") && f.weather?.length > 0 && (
          <Page ctx={ctx}>
            <SectionTitle ctx={ctx} n="B12" title="Schedule Notes & Weather" />
            <Prose>{s.schedule_notes}</Prose>
            <div className="mt-4"><WeatherCalendar data={f.weather} /></div>
            <FigCaption n="11">Seasonal working-condition calendar — {num(f.weather_total)} weather days lost expected</FigCaption>
          </Page>
        )}

        {/* B13 — clarifications */}
        {on("clarifications") && doc.clarifications?.length > 0 && (
          <Page ctx={ctx}><SectionTitle ctx={ctx} n="B13" title="Proposal Clarifications" /><NumberedList items={doc.clarifications} /></Page>
        )}
        </>)}

        {/* Thank you */}
        {on("closing") && (
        <Page ctx={ctx} className="flex flex-col justify-center">
          <div className="text-center" data-testid="proposal-thankyou">
            {b.logo ? <img src={b.logo} alt={company} style={{ maxHeight: 56, margin: "0 auto 20px" }} /> : null}
            <div className="font-display text-3xl font-bold" style={{ color: ink }}>Thank you for the opportunity</div>
            <p className="text-sm text-slate-600 max-w-md mx-auto mt-4 leading-relaxed">{s.closing}</p>
            <div className="mt-8 text-sm text-slate-700">
              <div className="font-semibold" style={{ color: primary }}>{company}</div>
              {[b.address, b.phone, b.email, b.website].filter(Boolean).map((l, i) => <div key={i} className="text-xs text-slate-500">{l}</div>)}
            </div>
            {b.footer && <div className="text-[10px] text-slate-400 mt-8">{b.footer}</div>}
          </div>
        </Page>
        )}
      </div>
    </div>
  );
}

/* ---------------- primitives ---------------- */
function Page({ ctx, children, className = "" }) {
  const { b, company, footer } = ctx;
  return (
    <div className={`mx-auto my-6 bg-white shadow print-page relative ${className}`} style={{ width: "8.5in", minHeight: "11in", padding: "0.75in" }}>
      <div className="absolute top-0 left-0 h-1.5 w-full" style={{ background: ctx.primary }} />
      {children}
      <div className="absolute bottom-6 left-[0.75in] right-[0.75in] flex justify-between text-[9px] text-slate-400 border-t border-slate-100 pt-1.5">
        <span>{ctx.company}</span><span>{ctx.b.footer || "Confidential"}</span>
      </div>
    </div>
  );
}

function Cover({ ctx, kicker, doc, perW, today, totalStr, testid }) {
  const { primary, accent, ink, company, b } = ctx;
  return (
    <div className="mx-auto my-6 bg-white shadow print-page flex flex-col overflow-hidden" style={{ width: "8.5in", minHeight: "11in" }} data-testid={testid}>
      {b.hero_photo && (
        <div className="w-full" style={{ height: "3.1in", overflow: "hidden" }} data-testid="proposal-cover-hero">
          <img src={b.hero_photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <div className="flex flex-col flex-1" style={{ padding: "0.75in" }}>
      <div className="pb-6 mb-8" style={{ borderBottom: `2px solid ${primary}` }}>
        {b.logo ? <img src={b.logo} alt={company} style={{ maxHeight: 56, marginBottom: 12 }} data-testid="proposal-brand-logo" />
          : <div className="font-mono text-xs tracking-widest mb-3" style={{ color: primary }} data-testid="proposal-brand-name">{(company || "").toUpperCase()}</div>}
        {b.tagline && <div className="text-xs font-semibold mb-3" style={{ color: accent }}>{b.tagline}</div>}
        <div className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: accent }}>{kicker}</div>
        <div className="font-display text-4xl font-bold leading-tight" style={{ color: ink, fontFamily: `'${ctx.headFont}',sans-serif` }}>{doc.name}</div>
        <div className="text-base text-slate-600 mt-3">{doc.location} · {num(doc.capacity_mw)} MW · {doc.discipline_scope}</div>
      </div>
      <div className="flex justify-between items-end mt-auto">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-400">Prepared for</div>
          {doc.client_logo ? <img src={doc.client_logo} alt={doc.client_name} style={{ maxHeight: 40, marginTop: 4, marginBottom: 4 }} data-testid="proposal-client-logo" /> : null}
          <div className="text-lg font-semibold" style={{ color: ink }}>{doc.client_name}</div>
          <div className="text-xs text-slate-500 mt-2">{today}</div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-slate-400">{kicker === "PROPOSAL" ? "Firm fixed price" : "Programme"}</div>
          <div className="font-display text-3xl font-bold" style={{ color: ink, fontFamily: `'${ctx.headFont}',sans-serif` }}>{totalStr}</div>
          <div className="text-sm text-slate-500 font-mono">{kicker === "PROPOSAL" ? perW : ""}</div>
        </div>
      </div>
      </div>
    </div>
  );
}

function SectionTitle({ ctx, n, title }) {
  return (
    <div className="flex items-baseline gap-2 mb-4 border-b border-slate-200 pb-2">
      <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded text-white" style={{ background: ctx.primary }}>{n}</span>
      <h2 className="font-display text-xl font-bold" style={{ color: ctx.ink, fontFamily: `'${ctx.headFont}',sans-serif` }}>{title}</h2>
    </div>
  );
}
function Prose({ children }) {
  if (!children) return null;
  return <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{children}</p>;
}
function Contact({ label, name, lines, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: accent }}>{label}</div>
      <div className="text-base font-semibold text-slate-800 mt-0.5">{name}</div>
      {(lines || []).filter(Boolean).map((l, i) => <div key={i} className="text-xs text-slate-500">{l}</div>)}
    </div>
  );
}
function NumberedList({ items }) {
  return <ol className="space-y-2">{items.map((t, i) => (
    <li key={i} className="text-sm text-slate-700 flex gap-3"><span className="font-mono text-slate-400 shrink-0">{i + 1}.</span><span>{t}</span></li>))}</ol>;
}
function KVTable({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">{title}</div>
      <table className="w-full text-sm">
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100"><td className="py-1.5 text-slate-600">{r.label}</td><td className="py-1.5 text-right font-mono font-medium">{r.value}</td></tr>))}</tbody>
      </table>
    </div>
  );
}
function DerivationTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <table className="w-full text-sm mb-2">
      <tbody>{rows.map((r, i) => (
        <tr key={i} className={`border-b border-slate-100 ${i === rows.length - 1 ? "font-semibold" : ""}`}>
          <td className="py-1.5 text-slate-600">{r.label}</td><td className="py-1.5 text-right font-mono">{r.value}</td></tr>))}</tbody>
    </table>
  );
}
function TotalLine({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center rounded px-4 py-2.5 mt-2" style={{ background: `${accent}22` }}>
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <span className="font-display font-bold text-lg text-slate-900">{value}</span>
    </div>
  );
}
function MiniStat({ label, value, ink }) {
  return (
    <div className="rounded border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-display text-xl font-bold mt-0.5" style={{ color: ink }}>{value}</div>
    </div>
  );
}
